import { SelectChangeEvent } from '@mui/material/Select'
import { ICountry } from 'country-state-city/lib/interface'
import { FormikHelpers } from 'formik'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Loader from '../../components/common/loader'
import {
    handleMiddleLayerAuthentication,
    middleLayerRequestOtp,
} from '../../services/auth'
import { Axios } from '../../services/axios'
import { MixPanel } from '../../services/mixpanelActions'
import { MESSAGES, USER_AUTHENTICATION_TYPE } from '../../utils/constants'
import {
    MIDDLELAYER_RESPONSE_CODE,
    ROUTES,
    SIGNUP_FORM_TYPE,
} from '../../utils/constants/enum'
import { OTP_LANGUAGE } from '../../utils/helper-functions'
import { useIpCountry } from '../../utils/hooks/useIpCountry'
import { getLocalState, useLocalState } from '../../utils/local-storage'
import {
    ISubAccount,
    IUnregisteredUserData,
    MultiAccountRequest,
    MultiAccountResponse,
    SignInFormTypes,
    ValidateOtpResponse,
} from '../../utils/types/auth'
import { UserDataContext } from './UserDataProvider'
import { emailRegex } from '../../utils/regex'

interface AuthContextType {
    isLoading: boolean
    otpSession: string | undefined
    countrySelected: ICountry | undefined
    setIsLoading: (value: boolean) => void
    handleSignIn: (
        values: SignInFormTypes,
        { setErrors }: FormikHelpers<SignInFormTypes>
    ) => void
    // handleOtpValidation: (otp: string) => Promise<void>
    handleOtpValidation: (code: string | MultiAccountRequest) => Promise<void>
    handleCountryChange: (event: SelectChangeEvent) => void
    handleResendOtp: () => Promise<void>
    multiAccount: MultiAccountResponse
    setMultiAccount: (value: MultiAccountResponse) => void
    allProvidersSignedUp: boolean | undefined
    setAllProvidersSignedUp: (value: boolean) => void
    selectedAccount: ISubAccount | null
    setSelectedAccount: (value: ISubAccount | null) => void
    signInDetails: SignInFormTypes | null
    unregisteredUserData: IUnregisteredUserData | null
    setUnregisteredUserData: (value: IUnregisteredUserData | null) => void
    clearAuthFlow: () => void
    userName: string
}

const isMultiAccountRequest = (data: any): data is MultiAccountRequest => {
    return (
        typeof data === 'object' &&
        'accessToken' in data &&
        'idToken' in data &&
        'refreshToken' in data &&
        'appUserKey' in data
    )
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType)

const AuthProvider = ({ children }: { children: ReactNode }) => {
    const { userData, setUserData } = useContext(UserDataContext)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [otpSession, setOtpSession] = useState<string>()
    const [multiAccount, setMultiAccount] = useState<MultiAccountResponse>(
        {} as MultiAccountResponse
    )
    const [userName, setUserName] = useState<string>('')
    const [countrySelected, setCountrySelected, countries, ipLoading] =
        useIpCountry()
    const [authToken] = useLocalState<ValidateOtpResponse>('auth-token')
    const [allProvidersSignedUp, setAllProvidersSignedUp] = useState<
        boolean | undefined
    >(undefined)
    const [selectedAccount, setSelectedAccount] = useState<ISubAccount | null>(
        null
    )
    const [signInDetails, setSignInDetails] = useState<SignInFormTypes | null>(
        null
    )
    const [unregisteredUserData, setUnregisteredUserData] =
        useState<IUnregisteredUserData | null>(null)

    const { t } = useTranslation()
    const navigate = useNavigate()
    const navigateRef = useRef(navigate)

    useEffect(() => {
        const localToken = getLocalState<ValidateOtpResponse>('auth-token')
        if (authToken && localToken) {
            navigateRef.current(ROUTES.DASHBOARD)
        }
    }, [authToken])

    const handleSignIn = useCallback(
        async (
            values: SignInFormTypes,
            { setErrors }: FormikHelpers<SignInFormTypes>
        ) => {
            const language = getLocalState<string>('i18nextLng', true)
            try {
                setIsLoading(true)
                setSignInDetails(values)
                const signInResponse = await middleLayerRequestOtp({
                    ...values,
                    username: emailRegex.test(values.username)
                        ? values.username
                              .trim()
                              .toLowerCase()
                              .replace(/[\u200B-\u200D\uFEFF\s]/g, '')
                        : values.username.trim().replace(/[-.\s()]/g, ''),

                    countrySelected: countrySelected?.isoCode,
                    language: OTP_LANGUAGE(language as string),
                })
                if (signInResponse?.data.session) {
                    setOtpSession(signInResponse?.data.session)
                    setUserName(
                        values.username
                            .trim()
                            .replace(/[\u200B-\u200D\uFEFF]/g, '')
                    )
                }
                return true
            } catch (error) {
                setErrors({
                    username: t('common_elements.something_wrong_text') || '',
                })
            } finally {
                setIsLoading(false)
            }
        },
        [countrySelected?.isoCode, t]
    )

    const handleResendOtp = useCallback(async () => {
        setIsLoading(true)
        if (!userName) {
            setOtpSession(undefined)
            return
        }
        const language = getLocalState<string>('i18nextLng', true)

        //MiddleLayer
        const signInResponse = await middleLayerRequestOtp({
            username: emailRegex.test(userName)
                ? userName
                      .trim()
                      .toLowerCase()
                      .replace(/[\u200B-\u200D\uFEFF\s]/g, '')
                : userName.trim().replace(/[-.\s()]/g, ''),
            countrySelected: countrySelected?.name,
            language: OTP_LANGUAGE(language as string),
            isResendRequest: true,
        })
        if (signInResponse?.data.session) {
            setOtpSession(signInResponse?.data.session)
        }
    }, [userName, countrySelected?.name])

    //MiddleLayer
    const handleOtpValidation = useCallback(
        async (data: string | MultiAccountRequest) => {
            if (!otpSession) {
                throw new Error('Invalid Session')
            }
            let response
            setIsLoading(true)
            if (isMultiAccountRequest(data)) {
                response = await handleMiddleLayerAuthentication(
                    {
                        ...data,
                    },
                    USER_AUTHENTICATION_TYPE.SIGNIN
                )
            } else {
                response = await handleMiddleLayerAuthentication(
                    {
                        code: data,
                        session: otpSession,
                    },
                    USER_AUTHENTICATION_TYPE.SIGNIN
                )
            }

            if (response.success) {
                if (
                    response.messageCode ===
                    MIDDLELAYER_RESPONSE_CODE.SUB_ACCOUNTS_FOUND
                ) {
                    const data = {
                        accessToken: response.data.accessToken,
                        idToken: response.data.idToken,
                        refreshToken: response.data.refreshToken,
                        subAccounts: response.data.subAccounts,
                    }
                    setMultiAccount(data)
                    setAllProvidersSignedUp(response.data.allProvidersSignedUp)
                    return
                }

                const {
                    data: {
                        token: jwtToken,
                        cumulusKeys: { idToken, accessToken, refreshToken },
                        refreshToken: middleLayerRefreshToken,
                    },
                } = response
                const tokens = {
                    jwtToken,
                    idToken,
                    accessToken,
                    refreshToken,
                    middleLayerRefreshToken,
                }
                Axios.initmiddleLayerInstance(tokens)
                setUserData(response.data)

                MixPanel.actions.people.set({
                    $first_name: response.data.firstName,
                    $last_name: response.data.lastName,
                    $email: response.data.email,
                    Role: response.data.userRole,
                })

                MixPanel.actions.identify(response.data.appUserKey)
                navigate(ROUTES.DASHBOARD)
            } else if (!response.success) {
                if (response?.data?.session) {
                    setOtpSession(response?.data?.session)
                }
                setIsLoading(false)
                if (
                    response.messageCode ===
                    MIDDLELAYER_RESPONSE_CODE.USER_DOES_NOT_EXIST
                ) {
                    navigateRef.current(ROUTES.SIGNUP)
                } else if (
                    response.messageCode ===
                    MIDDLELAYER_RESPONSE_CODE.INVALID_VERIFICATION_CODE
                ) {
                    throw new Error(MESSAGES.error.invalidVerificationCode)
                } else if (
                    response.messageCode ===
                    MIDDLELAYER_RESPONSE_CODE.INVALID_SESSION
                ) {
                    throw new Error(MESSAGES.error.verificationCodeExpired)
                } else if (
                    response.messageCode ===
                    MIDDLELAYER_RESPONSE_CODE.SOMETHING_WENT_WRONG
                ) {
                    throw new Error(
                        t('common_elements.something_wrong_text') ?? ''
                    )
                } else if (
                    response.messageCode ===
                    MIDDLELAYER_RESPONSE_CODE.USER_NOT_REGISTERED
                ) {
                    const data: IUnregisteredUserData = {
                        accessToken: response.data.accessToken,
                        idToken: response.data.idToken,
                        refreshToken: response.data.refreshToken,
                        isNewUser: response.data.isNewUser,
                    }
                    setUnregisteredUserData(data)
                    const signupUrl = `${ROUTES.SIGNUP}/${SIGNUP_FORM_TYPE.USER}`
                    return navigate(signupUrl)
                } else if (response.message) {
                    throw new Error(response.message)
                }
            }
        },
        [otpSession, navigate, setUserData]
    )

    const handleCountryChange = useCallback(
        (event: SelectChangeEvent) => {
            const countryCode = event.target.value
            setCountrySelected(
                countries.find(
                    (country) =>
                        country.isoCode.toLowerCase() ===
                        countryCode.toLowerCase()
                )
            )
        },
        [setCountrySelected, countries]
    )

    const clearAuthFlow = useCallback(() => {
        setOtpSession(undefined)
        if (!(signInDetails && Object.keys(multiAccount).length)) {
            setSignInDetails(null)
        }
        setUnregisteredUserData(null)
        setUserName('')
    }, [
        otpSession,
        signInDetails,
        unregisteredUserData,
        userName,
        multiAccount,
        setOtpSession,
        setSignInDetails,
        setUnregisteredUserData,
        setUserName,
    ])

    const mappedValue = useMemo(
        () => ({
            isLoading,
            otpSession,
            countrySelected,
            setIsLoading,
            handleCountryChange,
            handleSignIn,
            handleOtpValidation,
            handleResendOtp,
            userData,
            multiAccount,
            setMultiAccount,
            allProvidersSignedUp,
            setAllProvidersSignedUp,
            selectedAccount,
            setSelectedAccount,
            signInDetails,
            unregisteredUserData,
            setUnregisteredUserData,
            clearAuthFlow,
            userName,
        }),
        [
            isLoading,
            otpSession,
            countrySelected,
            setIsLoading,
            handleCountryChange,
            handleSignIn,
            handleOtpValidation,
            handleResendOtp,
            userData,
            multiAccount,
            setMultiAccount,
            setAllProvidersSignedUp,
            selectedAccount,
            setSelectedAccount,
            setSignInDetails,
            unregisteredUserData,
            setUnregisteredUserData,
            clearAuthFlow,
            userName,
        ]
    )

    return (
        <AuthContext.Provider value={mappedValue}>
            {ipLoading ? <Loader /> : <>{children}</>}
        </AuthContext.Provider>
    )
}

export default AuthProvider
